// ============================================
// COMPANY DETAIL PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, Building2, Star, MapPin, Globe, Loader2, X, Bell, BellOff } from 'lucide-react';
import { useCompany, useCompanyJobs, useCompanyReviews, useCreateReview, useIsFollowingCompany, useFollowCompany, useUnfollowCompany } from '@/hooks';
import { formatCurrency, getErrorMessage } from '@/utils';
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/companies/$slug')({
  component: CompanyDetailPage,
});

function CompanyDetailPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const params = Route.useParams() as { slug: string };
  const slug = params.slug;
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const reviewSchema = z.object({
    overallRating: z.number().min(1, t('panel:companyDetail.review.validation.ratingRequired')).max(5),
    salaryRating: z.number().min(1).max(5),
    workConditionRating: z.number().min(1).max(5),
    managementRating: z.number().min(1).max(5),
    foodRating: z.number().min(1).max(5),
    title: z.string().min(3, t('panel:companyDetail.review.validation.titleMin')),
    pros: z.string().min(10, t('panel:companyDetail.review.validation.prosMin')),
    cons: z.string().min(10, t('panel:companyDetail.review.validation.consMin')),
    position: z.string().min(2, t('panel:companyDetail.review.validation.positionRequired')),
    employmentYear: z.number().min(2000).max(new Date().getFullYear()),
    contractDuration: z.number().min(1).max(24),
    isAnonymous: z.boolean().default(true),
  });

  type ReviewFormData = z.infer<typeof reviewSchema>;

  const { data: companyResponse, isLoading } = useCompany(slug);
  const company: any = (companyResponse as any)?.data || companyResponse;
  
  const { data: jobsResponse } = useCompanyJobs(slug, { limit: 5 });
  const companyId = company?.id;
  const { data: reviewsResponse, refetch: refetchReviews } = useCompanyReviews(companyId || '', { enabled: !!companyId });
  const createReviewMutation = useCreateReview();
  const { data: isFollowing } = useIsFollowingCompany(companyId || '');
  const followMutation = useFollowCompany();
  const unfollowMutation = useUnfollowCompany();

  const handleFollowToggle = () => {
    if (!companyId) return;
    if (isFollowing) {
      unfollowMutation.mutate(companyId);
    } else {
      followMutation.mutate(companyId);
    }
  };

  const jobsData: any = (jobsResponse as any)?.data || jobsResponse || {};
  const jobsArray = jobsData?.data || jobsData?.jobs || jobsData;
  const jobs: any[] = Array.isArray(jobsArray) ? jobsArray : [];
  
  const reviewsData: any = (reviewsResponse as any)?.data || reviewsResponse || {};
  const reviewsArray = reviewsData?.data || reviewsData?.reviews || reviewsData;
  const reviews: any[] = Array.isArray(reviewsArray) ? reviewsArray : [];

  const {
    register, handleSubmit, setValue, watch, reset,
    formState: { errors },
  } = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      overallRating: 0, salaryRating: 3, workConditionRating: 3,
      managementRating: 3, foodRating: 3,
      employmentYear: new Date().getFullYear(), contractDuration: 6, isAnonymous: true,
    },
  });

  const overallRating = watch('overallRating');

  const onSubmitReview = (data: ReviewFormData) => {
    createReviewMutation.mutate(
      { ...data, companyId },
      {
        onSuccess: () => {
          toast.success(t('panel:companyDetail.review.success'));
          setIsReviewModalOpen(false);
          reset();
          refetchReviews();
        },
        onError: (error: any) => {
          toast.error(getErrorMessage(error, t('panel:companyDetail.review.error')));
        },
      }
    );
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-maritime-600" /></div>;
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">{t('panel:companyDetail.notFound')}</h2>
        <Link to="/companies" className="text-maritime-600 mt-2 inline-block">{t('panel:companyDetail.backToCompanies')}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Link to="/companies" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" />{t('panel:companyDetail.backToCompanies')}
      </Link>

      {/* Company Header */}
      <div className="card p-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 rounded-maritime bg-maritime-100 flex items-center justify-center flex-shrink-0">
            {company.logoUrl ? (
              <img src={company.logoUrl} alt={company.name} className="w-full h-full object-cover rounded-maritime" />
            ) : (
              <Building2 className="w-10 h-10 text-maritime-600" />
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-gray-500">
              {company.country && (
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{company.city}, {company.country}</span>
              )}
              {company.website && (
                <a href={company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-maritime-600 hover:text-maritime-700">
                  <Globe className="w-4 h-4" />{t('panel:companyDetail.website')}
                </a>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 mt-4">
              <div className="flex items-center gap-4">
                {company.averageRating && company.averageRating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                    <span className="font-semibold">{Number(company.averageRating).toFixed(1)}</span>
                    <span className="text-gray-500">({t('panel:companyDetail.reviewCount', { count: company.totalReviews || 0 })})</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleFollowToggle}
                disabled={followMutation.isPending || unfollowMutation.isPending}
                className={`flex items-center gap-2 px-4 py-2 rounded-maritime text-sm font-medium transition-colors ${
                  isFollowing
                    ? 'bg-maritime-50 text-maritime-700 border border-maritime-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300'
                    : 'bg-maritime-600 text-white hover:bg-maritime-700'
                }`}
              >
                {(followMutation.isPending || unfollowMutation.isPending) ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isFollowing ? (
                  <BellOff className="w-4 h-4" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
                {isFollowing ? t('panel:companyDetail.unfollow') : t('panel:companyDetail.follow')}
              </button>
            </div>
          </div>
        </div>

        {company.description && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-2">{t('panel:companyDetail.about')}</h3>
            <p className="text-gray-600">{company.description}</p>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
          {company.foundedYear && <InfoItem label={t('panel:companyDetail.founded')} value={String(company.foundedYear)} />}
          {company.employeeCount && <InfoItem label={t('panel:companyDetail.employees')} value={String(company.employeeCount)} />}
          {company.fleetSize && <InfoItem label={t('panel:companyDetail.fleet')} value={t('panel:companyDetail.vessels', { count: company.fleetSize })} />}
          <InfoItem label={t('panel:companyDetail.activeJobsLabel')} value={String(jobs.length)} />
        </div>
      </div>

      {/* Active Jobs */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{t('panel:companyDetail.openPositions')}</h2>
        </div>
        {jobs.length === 0 ? (
          <div className="p-6 text-center text-gray-500">{t('panel:companyDetail.noPositions')}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {jobs.map((job: any) => (
              <Link key={job.id} to="/jobs/$slug" params={{ slug: job.slug }} className="block p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{job.title}</h3>
                    <p className="text-sm text-gray-500">{job.vesselType ? t(`enums:vesselType.${job.vesselType}`, { defaultValue: job.vesselType }) : '-'}</p>
                  </div>
                  {job.minSalary && job.currency && (
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(job.minSalary, job.currency)}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Reviews */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{t('panel:companyDetail.reviews')}</h2>
          <button onClick={() => setIsReviewModalOpen(true)} className="text-sm text-maritime-600 hover:text-maritime-700 font-medium">
            {t('panel:companyDetail.writeReview')}
          </button>
        </div>
        {reviews.length === 0 ? (
          <div className="p-6 text-center text-gray-500">{t('panel:companyDetail.noReviews')}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {reviews.slice(0, 5).map((review: any) => (
              <div key={review.id} className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className={`w-4 h-4 ${star <= (review.overallRating || 0) ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-900">{review.title || ''}</span>
                </div>
                {review.pros && <p className="text-sm text-green-700 mb-1">✓ {review.pros}</p>}
                {review.cons && <p className="text-sm text-red-700">✗ {review.cons}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      <Dialog.Root open={isReviewModalOpen} onOpenChange={setIsReviewModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto z-50">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {t('panel:companyDetail.review.title', { company: company?.name })}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </Dialog.Close>
            </div>
            
            <form onSubmit={handleSubmit(onSubmitReview)} className="p-6 space-y-4">
              {/* Overall Rating */}
              <div>
                <label className="label">{t('panel:companyDetail.review.overallRating')} *</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} type="button" onClick={() => setValue('overallRating', star)} className="p-1">
                      <Star className={`w-8 h-8 transition-colors ${star <= overallRating ? 'text-amber-500 fill-amber-500' : 'text-gray-300 hover:text-amber-300'}`} />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-500">
                    {overallRating > 0 ? `${overallRating}/5` : t('common:select')}
                  </span>
                </div>
                {errors.overallRating && <p className="mt-1 text-xs text-red-500">{errors.overallRating.message}</p>}
              </div>

              {/* Detail Ratings */}
              <div className="grid grid-cols-2 gap-4">
                <RatingSelect label={t('panel:companyDetail.review.salary')} name="salaryRating" register={register} t={t} />
                <RatingSelect label={t('panel:companyDetail.review.workConditions')} name="workConditionRating" register={register} t={t} />
                <RatingSelect label={t('panel:companyDetail.review.management')} name="managementRating" register={register} t={t} />
                <RatingSelect label={t('panel:companyDetail.review.food')} name="foodRating" register={register} t={t} />
              </div>

              {/* Title */}
              <div>
                <label className="label">{t('panel:companyDetail.review.titleLabel')} *</label>
                <input type="text" className={`input ${errors.title ? 'input-error' : ''}`} placeholder={t('panel:companyDetail.review.titlePlaceholder')} {...register('title')} />
                {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>}
              </div>

              {/* Pros */}
              <div>
                <label className="label">{t('panel:companyDetail.review.pros')} *</label>
                <textarea className={`input min-h-[80px] ${errors.pros ? 'input-error' : ''}`} placeholder={t('panel:companyDetail.review.prosPlaceholder')} {...register('pros')} />
                {errors.pros && <p className="mt-1 text-xs text-red-500">{errors.pros.message}</p>}
              </div>

              {/* Cons */}
              <div>
                <label className="label">{t('panel:companyDetail.review.cons')} *</label>
                <textarea className={`input min-h-[80px] ${errors.cons ? 'input-error' : ''}`} placeholder={t('panel:companyDetail.review.consPlaceholder')} {...register('cons')} />
                {errors.cons && <p className="mt-1 text-xs text-red-500">{errors.cons.message}</p>}
              </div>

              {/* Position & Year */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('panel:companyDetail.review.position')} *</label>
                  <input type="text" className={`input ${errors.position ? 'input-error' : ''}`} placeholder="Chief Officer" {...register('position')} />
                </div>
                <div>
                  <label className="label">{t('panel:companyDetail.review.employmentYear')} *</label>
                  <input type="number" className="input" min={2000} max={new Date().getFullYear()} {...register('employmentYear', { valueAsNumber: true })} />
                </div>
              </div>

              {/* Contract Duration */}
              <div>
                <label className="label">{t('panel:companyDetail.review.contractDuration')}</label>
                <input type="number" className="input" min={1} max={24} {...register('contractDuration', { valueAsNumber: true })} />
              </div>

              {/* Anonymous */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="isAnonymous" className="w-4 h-4 text-maritime-600 rounded" {...register('isAnonymous')} />
                <label htmlFor="isAnonymous" className="text-sm text-gray-700">{t('panel:companyDetail.review.anonymous')}</label>
              </div>

              {/* Info */}
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                {t('panel:companyDetail.review.approvalNote')}
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Dialog.Close asChild>
                  <button type="button" className="btn-secondary">{t('common:cancel')}</button>
                </Dialog.Close>
                <button type="submit" className="btn-primary" disabled={createReviewMutation.isPending}>
                  {createReviewMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common:send')}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function RatingSelect({ label, name, register, t }: { label: string; name: string; register: any; t: any }) {
  return (
    <div>
      <label className="label text-sm">{label}</label>
      <select className="input text-sm" {...register(name, { valueAsNumber: true })}>
        <option value={1}>1 - {t('panel:companyDetail.review.rating1')}</option>
        <option value={2}>2 - {t('panel:companyDetail.review.rating2')}</option>
        <option value={3}>3 - {t('panel:companyDetail.review.rating3')}</option>
        <option value={4}>4 - {t('panel:companyDetail.review.rating4')}</option>
        <option value={5}>5 - {t('panel:companyDetail.review.rating5')}</option>
      </select>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="font-medium text-gray-900">{value}</p>
    </div>
  );
}
